import React from "react";
import { Chip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StarIcon from "@material-ui/icons/Star";

import { SiteCommissioningItem } from "../../../../../dtos/siteCommissioning";
import { DynamicFieldsList, formatValue, humanizeKey } from "./DynamicFieldsList";
import { PermissionNote } from "../console/PermissionNote";
import { tokens } from "../console/tokens";

const useStyles = makeStyles(() => ({
    note: {
        marginBottom: "12px",
    },
    card: {
        border: `1px solid ${tokens.line}`,
        borderRadius: "8px",
        padding: "12px",
        marginBottom: "12px",
        background: tokens.surface,
        "&:last-child": {
            marginBottom: 0,
        },
    },
    header: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        marginBottom: "8px",
    },
    interfaceId: {
        fontSize: "13px",
        fontWeight: 600,
    },
    primaryChip: {
        backgroundColor: tokens.accent,
        color: "#ffffff",
        fontWeight: 600,
    },
    primaryChipIcon: {
        color: "#ffffff !important",
    },
    linkStatusRow: {
        marginBottom: "8px",
    },
    fieldLabel: {
        color: tokens.hint,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontSize: "10.5px",
        fontWeight: 500,
        marginBottom: "2px",
    },
    tagRow: {
        marginBottom: "8px",
    },
    tagList: {
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
    },
    tag: {
        backgroundColor: tokens.canvas,
        color: tokens.muted,
        fontWeight: 500,
    },
}));

interface LinkStatusStyle {
    label: string;
    background: string;
    color: string;
}

const CONNECTING_STYLE = { background: tokens.waitBg, color: tokens.wait };

const LINK_STATUS_STYLES: Record<string, LinkStatusStyle> = {
    activated: { label: "Connected", background: tokens.okBg, color: tokens.ok },
    disconnected: { label: "Disconnected", background: tokens.dangerBg, color: tokens.danger },
    failed: { label: "Failed", background: tokens.dangerBg, color: tokens.danger },
    unavailable: { label: "Unavailable", ...CONNECTING_STYLE },
    unmanaged: { label: "Unmanaged", background: tokens.offBg, color: tokens.off },
    unknown: { label: "Unknown", background: tokens.offBg, color: tokens.off },
    deactivating: { label: "Disconnecting…", ...CONNECTING_STYLE },
    prepare: { label: "Connecting…", ...CONNECTING_STYLE },
    config: { label: "Connecting…", ...CONNECTING_STYLE },
    need_auth: { label: "Needs Authentication", ...CONNECTING_STYLE },
    ip_config: { label: "Connecting…", ...CONNECTING_STYLE },
    ip_check: { label: "Connecting…", ...CONNECTING_STYLE },
    secondaries: { label: "Connecting…", ...CONNECTING_STYLE },
};

function interfaceIdFromKey(key: string): string {
    return key.startsWith("networkInterface:") ? key.slice("networkInterface:".length) : key;
}

function simCardInfoEntries(simCardInfo: unknown): [string, unknown][] {
    if (simCardInfo == null || typeof simCardInfo !== "object" || Array.isArray(simCardInfo)) {
        return [];
    }
    return Object.entries(simCardInfo as Record<string, unknown>);
}

function linkStatusDisplay(linkStatus: unknown): LinkStatusStyle | undefined {
    if (linkStatus == null || linkStatus === "") {
        return undefined;
    }
    if (typeof linkStatus === "string" && LINK_STATUS_STYLES[linkStatus]) {
        return LINK_STATUS_STYLES[linkStatus];
    }
    return { label: humanizeKey(String(linkStatus)), background: tokens.offBg, color: tokens.off };
}

interface Props {
    items: Record<string, SiteCommissioningItem>;
}

export const NetworkInterfacesView: React.FC<Props> = ({ items }) => {
    const classes = useStyles();
    const entries = Object.entries(items);

    return (
        <>
            <div className={classes.note}>
                <PermissionNote>
                    Read-only. Network interfaces can only be added or changed from the commissioning site.
                </PermissionNote>
            </div>

            {entries.length === 0 ? (
                <Typography variant="body2">No network interfaces reported by the device yet.</Typography>
            ) : (
                entries.map(([key, item]) => {
                    const { linkStatus, primaryInternetInterface, simCardInfo, ...otherFields } = item.fields;
                    const status = linkStatusDisplay(linkStatus);
                    const simCardTags = simCardInfoEntries(simCardInfo);

                    return (
                        <div key={key} className={classes.card}>
                            <div className={classes.header}>
                                <Typography component="span" className={classes.interfaceId}>
                                    {interfaceIdFromKey(key)}
                                </Typography>
                                {primaryInternetInterface === true && (
                                    <Chip
                                        size="small"
                                        icon={<StarIcon className={classes.primaryChipIcon} fontSize="small" />}
                                        label="Primary Internet Interface"
                                        className={classes.primaryChip}
                                    />
                                )}
                            </div>
                            {status && (
                                <div className={classes.linkStatusRow}>
                                    <Typography variant="caption" className={classes.fieldLabel} component="div">
                                        Link Status
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={status.label}
                                        style={{ backgroundColor: status.background, color: status.color, fontWeight: 500 }}
                                    />
                                </div>
                            )}
                            {simCardTags.length > 0 && (
                                <div className={classes.tagRow}>
                                    <Typography variant="caption" className={classes.fieldLabel} component="div">
                                        Sim Card Info
                                    </Typography>
                                    <div className={classes.tagList}>
                                        {simCardTags.map(([tagKey, tagValue]) => (
                                            <Chip
                                                key={tagKey}
                                                size="small"
                                                label={`${humanizeKey(tagKey)}: ${formatValue(tagValue)}`}
                                                className={classes.tag}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            <DynamicFieldsList fields={otherFields} />
                        </div>
                    );
                })
            )}
        </>
    );
};
